/* ============================================
   NN COLLECTION FILTER
   Metaobject-driven facets (Lichtkegel / Helligkeit / Raum).

   These three facets are not Shopify native filter facets, so results.filters
   cannot express them. They run as a parallel client-side mechanism:

     - snippets/nn-collection-filter-data.liquid emits the facet definitions,
       each option carrying the product ids (Lichtkegel, Helligkeit) or the
       collection handles (Raum) that belong to it.
     - snippets/product-item.liquid stamps every card with data-nn-product-id
       and data-nn-collections.
     - This file inverts the facet data into product/collection -> option-id
       lookups and matches cards against the applied selection.

   Combination rules (Figma 7172:2596): several values inside one category
   combine with OR, across categories it is AND.

   Pagination: the grid is server-paginated, so filtering only the current page
   would be wrong. The first time a filter is applied the remaining pages are
   pulled once through the Section Rendering API and cached as a full card pool;
   from then on filtering and rendering happen entirely on the client, which is
   what "updates the grid without a full page reload" (Figma 7172:2631) asks for.

   Editorial break-out tiles (.bd-collection-grid-product-highlight-section)
   are not products and never enter that pool, but they're kept on the filtered
   grid too. The server positions them by an every-7th-of-all-products count
   computed while rendering the unfiltered page; that count means nothing once
   a filter has removed most of the products it was counted against, so the
   client recomputes the same every-7th cadence against the filtered result
   instead - position 7, 14, 21... of the MATCHED set, not the original one.
   The tiles themselves aren't re-rendered (no sale_section data reaches the
   client): every instance already rendered server-side, on the current page
   or any other page fetched for the pool, is collected into one ordered list
   and cycled through at each recomputed position, wrapping if there are more
   positions than captured tiles - the same rotate-through-a-short-list idea
   `sale_index` already applies server-side, just driven by what's on hand
   client-side rather than by the raw metaobject list. See `highlights` below.
   ============================================ */

(function () {
  'use strict';

  var CATEGORIES = ['lichtkegel', 'helligkeit', 'raum'];
  var PARAM_PREFIX = 'nn_';

  // Append ?nn_debug=1 to dump the resolved facet data, pool size and match
  // count - enough to tell a data problem from a matching problem in one reload.
  var NN_DEBUG = window.location.search.indexOf('nn_debug=1') !== -1;

  function emptyState() {
    return {
      lichtkegel: Object.create(null),
      helligkeit: Object.create(null),
      raum: Object.create(null)
    };
  }

  function keys(bag) {
    return Object.keys(bag);
  }

  function isEmpty(state) {
    return CATEGORIES.every(function (category) {
      return keys(state[category]).length === 0;
    });
  }

  function copyState(from, to) {
    CATEGORIES.forEach(function (category) {
      to[category] = Object.create(null);
      keys(from[category]).forEach(function (id) {
        to[category][id] = true;
      });
    });
  }

  function isCard(node) {
    return node.nodeType === 1 && node.hasAttribute('data-nn-product-id');
  }

  var HIGHLIGHT_CLASS = 'bd-collection-grid-product-highlight-section';

  // Keep in sync with the `current_index | modulo: 7` check in
  // sections/bd-main-collection-product-grid.liquid - that's the interval
  // this recomputes against the filtered result.
  var HIGHLIGHT_INTERVAL = 6;

  function isHighlight(node) {
    return node.nodeType === 1 && node.classList.contains(HIGHLIGHT_CLASS);
  }

  function init(section) {
    var dataEl = section.querySelector('[data-nn-filter-data]');
    var modal = section.querySelector('[data-nn-filter-modal]');

    if (!dataEl || !modal) return;
    if (!section.querySelector('.collection__products') || !section.querySelector('[data-partial]')) return;

    var facets;
    try {
      facets = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    /* --- Reverse index ---------------------------------------------------
       index[category].map: product id (or collection handle) -> option ids. */
    var index = Object.create(null);
    var validIds = Object.create(null);
    var hasFacets = false;

    CATEGORIES.forEach(function (category) {
      var facet = facets[category] || {};
      var options = facet.options || [];
      var map = Object.create(null);
      var valid = Object.create(null);

      options.forEach(function (option) {
        valid[option.id] = true;
        var members = facet.match === 'collection' ? option.collections : option.products;
        (members || []).forEach(function (member) {
          var key = String(member);
          if (!map[key]) map[key] = [];
          map[key].push(option.id);
        });
      });

      if (options.length) hasFacets = true;
      index[category] = { match: facet.match === 'collection' ? 'collection' : 'product', map: map };
      validIds[category] = valid;

      // A facet with entries but no members means the reference field was read
      // under the wrong key: the JSON is valid, the rows render, and every option
      // silently matches nothing. Say so rather than showing an empty grid.
      if (options.length && !Object.keys(map).length && window.console) {
        window.console.warn(
          '[nn-collection-filter] "' + category + '" has ' + options.length +
          ' option(s) but no ' + (facet.match === 'collection' ? 'collections' : 'products') +
          '. Check the reference field key in snippets/nn-collection-filter-data.liquid ' +
          'against the metaobject definition.'
        );
      }
    });

    if (!hasFacets) return;

    if (NN_DEBUG && window.console) {
      CATEGORIES.forEach(function (category) {
        var options = (facets[category] || {}).options || [];
        window.console.log(
          '[nn-collection-filter] ' + category + ':',
          options.map(function (option) {
            return option.id + ' (' + ((option.collections || option.products || []).length) + ')';
          })
        );
      });
    }

    var applied = emptyState();
    var draft = emptyState();

    var pool = null;
    var originalNodes = null;
    var hydration = null;
    var lastFocused = null;

    // Editorial break-out tiles, collected in page order from wherever they
    // were already rendered server-side (current page or any other page
    // fetched for the pool). Positioned client-side by cycling through this
    // list at every 7th of the FILTERED result - see render() - not tied to
    // any particular product, so filtering never has to decide whether one
    // "survives".
    var highlights = null;

    /* --- Live DOM references ---------------------------------------------
       The theme's native filtering() swaps [data-partial] wholesale, so the
       grid is re-resolved on every pass and anything cached from the previous
       DOM is dropped when the container identity changes. */
    var refs = { partial: null, grid: null, empty: null };

    function syncRefs() {
      var partial = section.querySelector('[data-partial]');
      if (partial !== refs.partial) {
        refs.partial = partial;
        pool = null;
        hydration = null;
        originalNodes = null;
        highlights = null;
      }
      refs.grid = section.querySelector('.collection__products');
      refs.empty = section.querySelector('[data-nn-empty]');
      return Boolean(refs.grid && refs.partial);
    }

    /* --- Matching --------------------------------------------------------- */

    function cardOptions(card, category) {
      if (!card.nnOptionCache) card.nnOptionCache = Object.create(null);
      if (card.nnOptionCache[category]) return card.nnOptionCache[category];

      var facet = index[category];
      var found = Object.create(null);

      if (facet.match === 'collection') {
        // Comma-joined by product-item.liquid; whitespace is tolerated too so a
        // stray separator never welds two handles into one unmatchable token.
        (card.getAttribute('data-nn-collections') || '').split(/[,\s]+/).forEach(function (handle) {
          if (!handle) return;
          (facet.map[handle] || []).forEach(function (id) {
            found[id] = true;
          });
        });
      } else {
        (facet.map[card.getAttribute('data-nn-product-id')] || []).forEach(function (id) {
          found[id] = true;
        });
      }

      card.nnOptionCache[category] = found;
      return found;
    }

    // AND across categories, OR inside one.
    function matches(card) {
      return CATEGORIES.every(function (category) {
        var selected = keys(applied[category]);
        if (!selected.length) return true;
        var own = cardOptions(card, category);
        return selected.some(function (id) {
          return own[id] === true;
        });
      });
    }

    /* --- Card pool (Section Rendering API) -------------------------------- */

    function pageUrl(page, sectionId) {
      var url = new URL(window.location.href);
      CATEGORIES.forEach(function (category) {
        url.searchParams.delete(PARAM_PREFIX + category);
      });
      url.searchParams.set('section_id', sectionId);
      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      return url.toString();
    }

    // Walks one page's grid children in original order, splitting them into
    // the product cards and the highlight tiles - both returned as flat
    // arrays, in the order they appeared. Shared by the current page and
    // every page fetched below, so the final list stays in page order
    // regardless of which order the fetches resolve in (see ensurePool).
    function collectPageChildren(children) {
      var cards = [];
      var tiles = [];

      Array.prototype.forEach.call(children, function (node) {
        if (isCard(node)) {
          cards.push(node);
        } else if (isHighlight(node)) {
          tiles.push(node);
        }
      });

      return { cards: cards, tiles: tiles };
    }

    function extractCards(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var remoteGrid = doc.querySelector('.collection__products');
      if (!remoteGrid) return { cards: [], tiles: [] };
      var imported = Array.prototype.map.call(remoteGrid.children, function (node) {
        return document.importNode(node, true);
      });
      return collectPageChildren(imported);
    }

    function ensurePool() {
      if (hydration) return hydration;

      originalNodes = Array.prototype.slice.call(refs.grid.children);

      var pages = parseInt(refs.partial.getAttribute('data-nn-pages'), 10) || 1;
      var current = parseInt(refs.partial.getAttribute('data-nn-page'), 10) || 1;
      var sectionId = section.getAttribute('data-section-id');
      var byPage = Object.create(null);

      byPage[current] = collectPageChildren(originalNodes);

      var pending = [];
      for (var page = 1; page <= pages; page++) {
        if (page === current) continue;
        pending.push(page);
      }

      hydration = Promise.all(
        pending.map(function (page) {
          return fetch(pageUrl(page, sectionId), { credentials: 'same-origin' })
            .then(function (response) {
              return response.ok ? response.text() : '';
            })
            .then(function (html) {
              byPage[page] = html ? extractCards(html) : { cards: [], tiles: [] };
            })
            .catch(function () {
              byPage[page] = { cards: [], tiles: [] };
            });
        })
      ).then(function () {
        pool = [];
        highlights = [];
        for (var page = 1; page <= pages; page++) {
          var entry = byPage[page] || { cards: [], tiles: [] };
          pool = pool.concat(entry.cards);
          highlights = highlights.concat(entry.tiles);
        }
        return pool;
      });

      return hydration;
    }

    /* --- Re-init cards pulled in from other pages -------------------------
       Mirrors the guarded product-grid initialisation in assets/custom.js so
       hydrated cards behave like the ones rendered server-side. */

    function initCards(scope) {
      scope.querySelectorAll('.collection-product-card').forEach(function (card) {
        if (card.nnCardBound) return;
        card.nnCardBound = true;
        card.addEventListener('click', function () {
          card.classList.toggle('expanded');
          card.classList.toggle('collapsed');
        });
      });

      if (typeof window.Swiper !== 'function') return;

      scope.querySelectorAll('.product_item_image.swiper').forEach(function (el) {
        if (el.classList.contains('swiper-initialized')) return;
        new window.Swiper(el, {
          direction: 'horizontal',
          loop: true,
          effect: 'slide',
          initialSlide: parseInt(el.getAttribute('data-initialSlide'), 10) || 0,
          on: {
            slideChangeTransitionEnd: function () {
              var active = this.el.querySelector('.swiper-slide.swiper-slide-active');
              if (!active) return;
              var media = active.getAttribute('data-variants_media');
              if (!media || media === ' ') return;
              var item = this.el.closest('.product-item');
              if (!item) return;
              var buttons = item.querySelectorAll('.product-item__color-variant');
              buttons.forEach(function (button) {
                if (media.indexOf(button.getAttribute('data-variant-id')) === -1) return;
                buttons.forEach(function (other) {
                  other.classList.remove('active');
                });
                button.classList.add('active');
                /* nn-fix: nur die Farbleiste horizontal scrollen, nie die Seite */ var nnRow = button.closest('.product-item__color-variants-inner'); if (nnRow) nnRow.scrollBy({ left: button.getBoundingClientRect().left - nnRow.getBoundingClientRect().left, behavior: 'smooth' });
              });
            }
          }
        });
      });
    }

    /* --- URL state --------------------------------------------------------
       Not required by the dev notes; kept as replaceState only so a filtered
       grid stays shareable and survives a reload without a server round-trip. */

    function writeUrl() {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      CATEGORIES.forEach(function (category) {
        var values = keys(applied[category]);
        if (values.length) {
          url.searchParams.set(PARAM_PREFIX + category, values.join(','));
        } else {
          url.searchParams.delete(PARAM_PREFIX + category);
        }
      });
      window.history.replaceState({}, '', url.toString());
    }

    function readUrl() {
      var params = new URLSearchParams(window.location.search);
      CATEGORIES.forEach(function (category) {
        var raw = params.get(PARAM_PREFIX + category);
        if (!raw) return;
        raw.split(',').forEach(function (value) {
          if (value && validIds[category][value]) applied[category][value] = true;
        });
      });
    }

    /* --- Rendering -------------------------------------------------------- */

    function syncPills() {
      section.querySelectorAll('[data-nn-open]').forEach(function (pill) {
        var category = pill.getAttribute('data-nn-open');
        pill.classList.toggle('is-active', keys(applied[category]).length > 0);
      });
    }

    function restore() {
      section.classList.remove('nn-filter-active', 'nn-filter-loading');
      if (refs.empty) refs.empty.hidden = true;
      refs.grid.hidden = false;
      if (!originalNodes) return;
      refs.grid.textContent = '';
      originalNodes.forEach(function (node) {
        refs.grid.appendChild(node);
      });
    }

    function render() {
      syncPills();
      writeUrl();

      if (!syncRefs()) return Promise.resolve();

      if (isEmpty(applied)) {
        restore();
        return Promise.resolve();
      }

      section.classList.add('nn-filter-loading');

      return ensurePool().then(function (cards) {
        var matched = cards.filter(matches);

        // An empty pool means no child of .collection__products carries
        // data-nn-product-id, which filters everything out no matter what the
        // metaobjects say. Almost always a product-item.liquid deploy gap.
        if (!cards.length && window.console) {
          window.console.warn(
            '[nn-collection-filter] card pool is empty - nothing in .collection__products ' +
            'carries data-nn-product-id. Check that the updated snippets/product-item.liquid ' +
            'is deployed to this theme.'
          );
        }

        if (NN_DEBUG && window.console) {
          window.console.log('[nn-collection-filter] applied:', {
            lichtkegel: keys(applied.lichtkegel),
            helligkeit: keys(applied.helligkeit),
            raum: keys(applied.raum)
          });
          window.console.log('[nn-collection-filter] pool:', cards.length, 'matched:', matched.length);
          if (cards[0]) {
            window.console.log(
              '[nn-collection-filter] sample card - id:', cards[0].getAttribute('data-nn-product-id'),
              '| collections:', cards[0].getAttribute('data-nn-collections')
            );
          }
        }

        refs.grid.textContent = '';
        var highlightCursor = 0;
        matched.forEach(function (card, matchedIndex) {
          refs.grid.appendChild(card);

          // Mirrors the server's "every 7th product" cadence
          // (sections/bd-main-collection-product-grid.liquid: current_index
          // modulo 7), but counted against the FILTERED result instead of all
          // of collection.products - that original count means nothing once
          // most of what it was counted against has been filtered out.
          var position = matchedIndex + 1;
          if (highlights.length && position % HIGHLIGHT_INTERVAL === 0) {
            refs.grid.appendChild(highlights[highlightCursor % highlights.length]);
            highlightCursor++;
          }
        });
        initCards(refs.grid);

        section.classList.add('nn-filter-active');
        section.classList.remove('nn-filter-loading');

        // The empty state replaces the grid (Figma 7172:2188).
        refs.grid.hidden = matched.length === 0;
        if (refs.empty) refs.empty.hidden = matched.length > 0;
      });
    }

    /* --- Modal ------------------------------------------------------------ */

    var inputs = Array.prototype.slice.call(modal.querySelectorAll('[data-nn-option]'));
    var tabs = Array.prototype.slice.call(modal.querySelectorAll('[data-nn-tab]'));
    var groups = Array.prototype.slice.call(modal.querySelectorAll('[data-nn-group]'));
    var applyButton = modal.querySelector('[data-nn-apply]');

    function syncInputs() {
      inputs.forEach(function (input) {
        var category = input.getAttribute('data-nn-category');
        input.checked = draft[category][input.value] === true;
      });
      syncApplyState();
    }

    // Disabled while nothing is selected (Figma 7172:2433). Kept enabled when
    // something is still applied, otherwise a cleared selection could never be
    // committed and the grid would be stuck in its filtered state.
    function syncApplyState() {
      if (!applyButton) return;
      applyButton.disabled = isEmpty(draft) && isEmpty(applied);
    }

    // Mobile shows one category at a time; switching tabs keeps the unapplied
    // selections of the others (Figma 7172:2384). Desktop shows all groups.
    function setTab(category) {
      tabs.forEach(function (tab) {
        tab.setAttribute('aria-selected', String(tab.getAttribute('data-nn-tab') === category));
      });
      groups.forEach(function (group) {
        group.classList.toggle('is-open', group.getAttribute('data-nn-group') === category);
      });
    }

    function openModal(category) {
      lastFocused = document.activeElement;
      copyState(applied, draft);
      syncInputs();
      setTab(category);

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      window.requestAnimationFrame(function () {
        modal.classList.add('is-open');
      });

      section.querySelectorAll('[data-nn-open]').forEach(function (pill) {
        pill.setAttribute('aria-expanded', String(pill.getAttribute('data-nn-open') === category));
      });

      var focusTarget = modal.querySelector('[data-nn-tab][aria-selected="true"]') || modal.querySelector('[data-nn-close]');
      if (focusTarget) focusTarget.focus();
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      section.querySelectorAll('[data-nn-open]').forEach(function (pill) {
        pill.setAttribute('aria-expanded', 'false');
      });

      window.setTimeout(function () {
        if (!modal.classList.contains('is-open')) modal.hidden = true;
      }, 300);

      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    /* --- Events ----------------------------------------------------------- */

    section.querySelectorAll('[data-nn-open]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        openModal(pill.getAttribute('data-nn-open'));
      });
    });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        setTab(tab.getAttribute('data-nn-tab'));
      });
    });

    // The whole row is the label, so a click on either the row or the box
    // toggles the value and a second click deselects (Figma 7172:2597).
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var category = input.getAttribute('data-nn-category');
        if (input.checked) {
          draft[category][input.value] = true;
        } else {
          delete draft[category][input.value];
        }
        syncApplyState();
      });
    });

    // Reset is a live action, not a staged edit: it clears every category and
    // re-renders the grid immediately, same as Apply does, just without closing
    // the modal. Previously the per-tab button only cleared its own category in
    // draft, so a full clear took one tap per tab and never touched applied -
    // the grid didn't update, and reopening the modal re-synced draft from the
    // still-populated applied state. Both mobile's per-tab button and desktop's
    // reset-all link now do the same full clear, one tap either way.
    modal.querySelectorAll('[data-nn-reset], [data-nn-reset-all]').forEach(function (button) {
      button.addEventListener('click', function () {
        draft = emptyState();
        applied = emptyState();
        syncInputs();
        render();
      });
    });

    // Applies all three axes at once and closes (Figma 7172:2631).
    if (applyButton) {
      applyButton.addEventListener('click', function () {
        if (applyButton.disabled) return;
        copyState(draft, applied);
        closeModal();
        render();
      });
    }

    modal.querySelectorAll('[data-nn-close]').forEach(function (button) {
      button.addEventListener('click', closeModal);
    });

    modal.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.keyCode === 27) closeModal();
    });

    // The theme's native "clear filters" button should drop these facets too.
    section.addEventListener('click', function (event) {
      if (!event.target.closest('[data-clear="true"]')) return;
      applied = emptyState();
      render();
    });

    /* --- Boot ------------------------------------------------------------- */

    readUrl();
    if (!isEmpty(applied)) {
      render();
    } else {
      syncPills();
    }
  }

  function boot() {
    document.querySelectorAll('[data-section-type="collection"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
